import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Field } from "@base-ui/react/field";
import { Input } from "@base-ui/react/input";
import { DismissIcon } from "../../primitives/Icons/Icons";
import shared from "../../shared.module.css";
import css from "./SearchInput.module.css";

const SearchIcon = () => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    className={css.searchIcon}
  >
    <path
      d="M17.5 17.5L13.875 13.875M15.833 9.167a6.667 6.667 0 1 1-13.333 0 6.667 6.667 0 0 1 13.333 0Z"
      stroke="currentColor"
      strokeWidth="1.667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SearchInput = React.forwardRef(
  (
    {
      bare = false,
      value,
      onValueChange,
      onClear,
      placeholder = "Search\u2026",
      label = "Search",
      labelVisible = false,
      description,
      error,
      trailing,
      disabled = false,
      className,
      ...rest
    },
    ref,
  ) => {
    const showClear = Boolean(value);
    const hasError = Boolean(error);

    const handleClear = () => {
      if (onClear) {
        onClear();
      } else if (onValueChange) {
        onValueChange("");
      }
    };

    if (bare) {
      return (
        <div className={css.inputWrapper}>
          <SearchIcon />
          <Input
            ref={ref}
            className={css.input}
            type="search"
            placeholder={placeholder}
            value={value}
            onValueChange={onValueChange}
            disabled={disabled}
            {...rest}
          />
          {showClear && (
            <button
              type="button"
              className={css.clearBtn}
              onClick={handleClear}
              aria-label="Clear search"
            >
              <DismissIcon />
            </button>
          )}
        </div>
      );
    }

    return (
      <Field.Root
        className={clsx(css.root, className)}
        disabled={disabled}
        invalid={hasError}
      >
        <Field.Label
          className={labelVisible ? css.label : shared.visuallyHidden}
        >
          {label}
        </Field.Label>
        <div className={css.inputWrapper}>
          <SearchIcon />
          <Input
            ref={ref}
            className={css.input}
            type="search"
            placeholder={placeholder}
            value={value}
            onValueChange={onValueChange}
            {...rest}
          />
          {showClear && (
            <button
              type="button"
              className={css.clearBtn}
              onClick={handleClear}
              aria-label="Clear search"
            >
              <DismissIcon />
            </button>
          )}
          {trailing}
        </div>
        {description && !hasError && (
          <Field.Description className={css.description}>
            {description}
          </Field.Description>
        )}
        {typeof error === "string" && (
          <Field.Error className={css.error} match>
            {error}
          </Field.Error>
        )}
      </Field.Root>
    );
  },
);

SearchInput.displayName = "SearchInput";

SearchInput.propTypes = {
  bare: PropTypes.bool,
  value: PropTypes.string,
  onValueChange: PropTypes.func,
  onClear: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  labelVisible: PropTypes.bool,
  description: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  trailing: PropTypes.node,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default SearchInput;
